/**
 * Date Range Filter Component
 * Dropdown for selecting date range with custom date picker support
 */

import React, { useState, useRef, useEffect } from 'react'
import { Calendar, ChevronDown } from 'lucide-react'

export type DateRangeOption = 'today' | 'last_7_days' | 'last_30_days' | 'this_month' | 'custom'

interface DateRangeFilterProps {
  selectedRange: DateRangeOption
  customStartDate?: Date
  customEndDate?: Date
  onChange: (range: DateRangeOption, startDate?: Date, endDate?: Date) => void
}

const DateRangeFilter: React.FC<DateRangeFilterProps> = ({
  selectedRange,
  customStartDate,
  customEndDate,
  onChange
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [showCustomPicker, setShowCustomPicker] = useState(false)
  const [tempStartDate, setTempStartDate] = useState<string>('')
  const [tempEndDate, setTempEndDate] = useState<string>('')
  const [error, setError] = useState<string>('')
  const dropdownRef = useRef<HTMLDivElement>(null)

  const options: { value: DateRangeOption; label: string }[] = [
    { value: 'today', label: 'Today' },
    { value: 'last_7_days', label: 'Last 7 Days' },
    { value: 'last_30_days', label: 'Last 30 Days' },
    { value: 'this_month', label: 'This Month' },
    { value: 'custom', label: 'Custom Range...' }
  ]

  const getSelectedLabel = () => {
    if (selectedRange === 'custom' && customStartDate && customEndDate) {
      return `${customStartDate.toLocaleDateString()} - ${customEndDate.toLocaleDateString()}`
    }
    return options.find(opt => opt.value === selectedRange)?.label || 'Select Range'
  }

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleOptionSelect = (option: DateRangeOption) => {
    if (option === 'custom') {
      setShowCustomPicker(true)
      setIsOpen(false)
      // Initialize with current custom dates if available
      if (customStartDate) {
        setTempStartDate(customStartDate.toISOString().split('T')[0])
      }
      if (customEndDate) {
        setTempEndDate(customEndDate.toISOString().split('T')[0])
      }
    } else {
      onChange(option)
      setIsOpen(false)
    }
  }

  const handleCustomDateSubmit = () => {
    setError('')

    if (!tempStartDate || !tempEndDate) {
      setError('Both start and end dates are required')
      return
    }

    // Create dates in local timezone (not UTC)
    // Parse the date string and create a date at midnight local time
    const [startYear, startMonth, startDay] = tempStartDate.split('-').map(Number)
    const [endYear, endMonth, endDay] = tempEndDate.split('-').map(Number)
    
    const startDate = new Date(startYear, startMonth - 1, startDay, 0, 0, 0, 0)
    const endDate = new Date(endYear, endMonth - 1, endDay, 23, 59, 59, 999)

    if (startDate > endDate) {
      setError('Start date must be before end date')
      return
    }

    const endOfToday = new Date()
    endOfToday.setHours(23, 59, 59, 999)
    if (endDate > endOfToday) {
      setError('End date cannot be in the future')
      return
    }

    const daysDiff = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24))
    if (daysDiff > 365) {
      setError('Date range cannot exceed 365 days')
      return
    }

    onChange('custom', startDate, endDate)
    setShowCustomPicker(false)
    setTempStartDate('')
    setTempEndDate('')
  }

  const handleCustomDateCancel = () => {
    setShowCustomPicker(false)
    setTempStartDate('')
    setTempEndDate('')
    setError('')
  }

  return (
    <>
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        >
          <Calendar className="w-4 h-4 mr-2" />
          {getSelectedLabel()}
          <ChevronDown className="w-4 h-4 ml-2" />
        </button>

        {isOpen && (
          <div className="absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
            <div className="py-1" role="menu">
              {options.map((option) => (
                <button
                  key={option.value}
                  onClick={() => handleOptionSelect(option.value)}
                  className={`
                    block w-full text-left px-4 py-2 text-sm
                    ${
                      selectedRange === option.value && option.value !== 'custom'
                        ? 'bg-primary-50 text-primary-700'
                        : 'text-gray-700 hover:bg-gray-100'
                    }
                  `}
                  role="menuitem"
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Custom Date Picker Modal */}
      {showCustomPicker && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Select Custom Date Range
            </h3>

            <div className="space-y-4">
              <div>
                <label htmlFor="start-date" className="block text-sm font-medium text-gray-700 mb-1">
                  Start Date
                </label>
                <input
                  type="date"
                  id="start-date"
                  value={tempStartDate}
                  onChange={(e) => setTempStartDate(e.target.value)}
                  max={new Date().toISOString().split('T')[0]}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div>
                <label htmlFor="end-date" className="block text-sm font-medium text-gray-700 mb-1">
                  End Date
                </label>
                <input
                  type="date"
                  id="end-date"
                  value={tempEndDate}
                  onChange={(e) => setTempEndDate(e.target.value)}
                  max={new Date().toISOString().split('T')[0]}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              {error && (
                <div className="text-sm text-red-600">
                  {error}
                </div>
              )}
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={handleCustomDateCancel}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                Cancel
              </button>
              <button
                onClick={handleCustomDateSubmit}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default DateRangeFilter
